import { generatePath } from 'react-router';
import { v4 as uuid } from 'uuid';
import { sourceKeyMapping, paths } from '@AppRoutes/constants';
import { generateHeatmapPath, generateResultsPath } from '@AppRoutes/utils';
import history from '@AppRoutes/history';
import * as AuthUtils from '@Services/Auth/AuthUtils';
import AppStorage from '@Services/AppStorage';
import BatchExecutionDetails from '@Services/BatchExecutionDetails';
import Breadcrumb from '@Services/Breadcrumb';
import History from '@Services/History';
import HistoryHelper from '@Services/HistoryHelper';
import loginConstants from '@Services/Login/LoginConstants';
import ResultsInfo from '@Services/ResultsInfo';
import { SearchTypes } from '@Pages/History/Constants';
import each from 'lodash/each';
import findLastIndex from 'lodash/findLastIndex';
import extend from 'lodash/extend';
import { startGenAIContentProcessingTimer } from '@Containers/Summary/analytics';

let visitedStates = [];

const getTextQueryFromStorage = (searchId) => {
  const semanticQueryBySearchId = AppStorage.instance.getFromSessionStorage('qbSemanticQueryBySearchId') || {};

  if (!searchId || !semanticQueryBySearchId?.[searchId]) {
    return '';
  }

  const textQuery = semanticQueryBySearchId[searchId];
  const remainingQueries = { ...semanticQueryBySearchId };
  delete remainingQueries[searchId];
  AppStorage.instance.setToSessionStorage('qbSemanticQueryBySearchId', remainingQueries);
  return textQuery;
};

const withSemanticFallback = (item) => {
  const textQuery = item?.textQuery || getTextQueryFromStorage(item?.searchId);

  // An explicit false means the search ran without semantic results (e.g. a saved search whose
  // row still stores its textQuery) — never re-flag it, or the semantic summary API gets called.
  if (!textQuery || item?.semanticSearchIncluded === false) {
    return item;
  }

  return {
    ...item,
    semanticSearchIncluded: true,
    semanticResultName: item?.semanticResultName || item?.resultName,
    textQuery,
  };
};

const isQuickSearchRecentItem = (item = {}) =>
  !item?.searchName &&
  !item?.isSessionOnlyRecent &&
  (Boolean(item?.quickSearchItem) || item?.historyType === 'quickSearch' || item?.historyType === SearchTypes.QUICK_SEARCH);

const isBatchRecentItem = (item = {}) =>
  !item?.searchName && (item?.historyType === 'batchSearch' || item?.historyType === SearchTypes.BATCH_SEARCH);

// Query Builder recent rows carry the original queryFactItem, so they can be re-executed over
// the AdvancedQuery WebSocket channel instead of `restore/results`, which cannot restore result
// sets from a previous session.
const isAdvancedSearchRecentItem = (item = {}) =>
  !item?.searchName &&
  !item?.isSessionOnlyRecent &&
  Boolean(item?.queryFactItem) &&
  (item?.historyType === 'queryBuilder' || item?.historyType === SearchTypes.ADVANCED_SEARCH);

const getQueryCount = (queries) => {
  if (Array.isArray(queries)) {
    return queries.length;
  }
  if (queries && typeof queries === 'object') {
    return Object.keys(queries).length;
  }
  return 0;
};

const getBatchFileName = (batchDetails, historyItem) => {
  if (batchDetails?.filename || historyItem?.batchFileName) {
    return batchDetails?.filename || historyItem?.batchFileName;
  }

  const sourceText = historyItem?.description || historyItem?.comment || '';
  const fileNameMatch = sourceText.match(/Search from file "([^"]+)"/);
  return fileNameMatch?.[1] || historyItem?.searchName || 'batch search';
};

const getRerunSourceRecentHistory = (source = {}) => ({
  id: source?.id,
  databaseId: source?.databaseId,
  context: source?.context,
  searchId: source?.searchId,
  resultName: source?.resultName,
  creationTime: source?.creationTime,
  userTime: source?.userTime,
  historyType: source?.historyType,
  filters: source?.filters,
  facets: source?.facets,
  sort: source?.sort,
  order: source?.order,
  isSessionOnlyRecent: source?.isSessionOnlyRecent,
  batchFileName: source?.batchFileName,
});

export async function rerunBatchSearchHistoryItem(historyItem) {
  // Lazy-load to break the module-init cycle: BatchSearchRunner → SearchProgress → Search → Auth →
  // AuthCleanUp → NavigationHistory → BatchSearchRunner. The rule still counts import() as an edge.
  // eslint-disable-next-line import/no-cycle
  const { default: BatchSearchRunner } = await import('@Services/BatchSearchRunner');
  const batchDetails = await BatchExecutionDetails.getById(historyItem?.searchId, historyItem?.databaseId);
  const queries = batchDetails?.queries || historyItem?.queries;

  if (!queries || getQueryCount(queries) === 0) {
    const error = new Error('Unable to rerun this batch search.');
    error.component = 'NavigationHistory';
    throw error;
  }

  // Match the original batch search payload shape (BatchModalActions.send):
  // only context, searchId, queries, and optionally options.
  // Do NOT include databases/databaseId — the original search lets the backend
  // decide which databases to process; restricting to one database breaks
  // derived-context reruns (e.g. tgitems stored with a single databaseId).
  const payload = {
    context: batchDetails?.context || historyItem?.context,
    searchId: uuid(),
    queries,
    ...(batchDetails?.options && { options: batchDetails.options }),
    ...(historyItem?.sort && { sort: historyItem.sort }),
    ...(historyItem?.order && { order: historyItem.order }),
  };

  const batchFile = {
    filename: getBatchFileName(batchDetails, historyItem),
    queries,
    validQueriesCount: getQueryCount(queries),
    rerunSourceRecentHistory: getRerunSourceRecentHistory(historyItem?.rerunSourceRecentHistory || historyItem),
  };

  return BatchSearchRunner.sendRequest(payload, batchFile);
}

export const init = () => {
  const currentUser = AuthUtils?.getUser() || {};
  if (!currentUser.history) {
    Object.assign(currentUser, {
      history: {
        breadcrumbs: {},
        viewedResultsParams: {},
        recentSearches: [],
        savedSearches: [],
      },
    });
  }
  AuthUtils.updateUser(currentUser);
};

export const addState = (state, params) => {
  const stateObj = {
    name: state,
    params,
  };
  visitedStates.push(stateObj);
};

// Updated for React routes
export const getStatePathName = (state) => {
  if (!state) return null;

  const _statePath = Object.keys(sourceKeyMapping).find((_path) => sourceKeyMapping[_path] === state.name);
  if (_statePath === paths.RESULTS) {
    return generateResultsPath(state.params);
  }
  if (_statePath === paths.HEATMAP) {
    return generateHeatmapPath({ ...state.params, isHeatmap: true });
  }
  return generatePath(_statePath, state.params);
};

export const getLastVisitedState = (...args) => {
  if (!visitedStates.length) {
    return null;
  }

  let lastStateIndex = null;
  let lastState = null;

  each(args, (proposedState) => {
    const proposedStateIndex = findLastIndex(visitedStates, (state) => state && state.name === proposedState);

    if (proposedStateIndex >= 0 && (lastStateIndex === null || proposedStateIndex > lastStateIndex)) {
      lastStateIndex = proposedStateIndex;
      lastState = visitedStates[lastStateIndex];
    }
  });
  return (
    lastState && {
      ...lastState,
      pathName: getStatePathName(lastState),
    }
  );
};

export const getLastVisitedStateForLogin = () => getLastVisitedState(...loginConstants.STATE_WHITELIST);

const loadHistoryResult = (item) => {
  // Session-only recent entries (filter/sort/order changes never persisted to the backend) do not
  // exist on the server, so calling `restore/results` returns 404. Resolve locally instead.
  if (item?.isSessionOnlyRecent) {
    return Promise.resolve(item);
  }

  if (isQuickSearchRecentItem(item)) return History.rerunQuickSearchHistoryItem(item);
  if (isAdvancedSearchRecentItem(item)) return History.rerunAdvancedSearchHistoryItem(item);
  return History.restoreHistory(item);
};

export function viewHistory(item) {
  const sourceHistoryItem = { ...(item?.rerunSourceRecentHistory || item) };
  const isBatchItem = isBatchRecentItem(item) || item?.historyType === 'batch';

  // Batch rows are session-only (sessionStorage) and carry their `queries`, so reruns always
  // re-execute through the runner. There is no backend restore fallback: the backend does not
  // round-trip batch queries, so persisted batch rows do not exist yet.
  if (isBatchItem) {
    return rerunBatchSearchHistoryItem(item);
  }

  const resultSize = item?.resultSize ?? item?.totalResult ?? item?.recordCount;
  if (resultSize === 0) {
    return Promise.resolve();
  }

  // Reruns overlay the full row (query fields like quickSearchItem stay intact) with the
  // preserved source identity (searchId/resultName/databaseId) so backend restore and rerun
  // matching use the original persisted identity. Session-only rows keep their local identity.
  const itemToLoad = item?.isSessionOnlyRecent ? item : { ...item, ...sourceHistoryItem };
  return (
    loadHistoryResult(itemToLoad)
      // eslint-disable-next-line no-shadow
      .then((item) => {
        const historyItem = {
          ...withSemanticFallback(item),
          isHistoryBreadcrumb: true,
          // A genuine re-execution refreshes the source Recent row timestamp. Session-only rows
          // still navigate locally, then storeRecentHistory updates their session-only recency overlay.
          isRerun: !item?.isSessionOnlyRecent,
          rerunSourceRecentHistory: getRerunSourceRecentHistory(sourceHistoryItem),
        };
        Breadcrumb.resetBreadcrumbs();
        ResultsInfo.clearResultsInfo();
        const params = {
          databaseId: historyItem.databaseId,
          context: historyItem.context,
          resultName: historyItem.resultName,
          searchId: historyItem.searchId,
          pageNumber: 1,
          sort: '',
          hitSetKey: historyItem.hitSetKey,
        };
        Breadcrumb.activateBreadcrumb(historyItem.resultName, historyItem.databaseId);
        return Promise.resolve(
          Breadcrumb.createBreadcrumb(
            historyItem,
            historyItem.resultSize ?? historyItem.totalResult ?? historyItem.recordCount,
            historyItem.filters
          )
        )
          .then(() => {
            if (item?.isSessionOnlyRecent) {
              HistoryHelper.storeRecentHistory({ ...historyItem, isRerun: true });
            }

            if (historyItem.isHeatmapShown) {
              extend(params, {
                sortedMatrixName: historyItem.sortedMatrixName,
                index: historyItem.index || null,
                isRow: historyItem.isRow || null,
                ascending: historyItem.ascending || null,
                fromContext: historyItem.fromContext,
                context: 'dpitems',
                hitSetKey: historyItem.hitSetKey,
                resultName: historyItem.heatmapResultName,
                searchId: historyItem.searchId,
                order: historyItem.order,
                sort: historyItem.sort,
              });
            }

            if (historyItem.semanticSearchIncluded) {
              extend(params, {
                semanticSearchIncluded: true,
                semanticResultName: historyItem.semanticResultName || '',
                textQuery: historyItem.textQuery || '',
              });
            }

            Breadcrumb.updateActiveParams(null); // reset active resultName
            const pathName = historyItem.isHeatmapShown ? generateHeatmapPath(params) : generateResultsPath(params);

            if (params.semanticSearchIncluded) {
              startGenAIContentProcessingTimer();
            }

            history.push({
              pathname: pathName,
              state: { ...params, order: historyItem.order, sort: historyItem.sort },
            });
          })
          .catch((error) => {
            Logger.error(error);
            throw error;
          });
      })
      .catch((error) => {
        Logger.error(error);
        throw error;
      })
  );
}

export const softClear = () => {
  visitedStates.length = 1;
};

export const hardClear = () => {
  visitedStates = [];
};

export const importVisitedStates = (states) => {
  visitedStates = states;
};
